import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import User from 'flarum/common/models/User';
import UserSearch from './UserSearch';
import Radio from 'flarum/common/components/Radio';

export default class ReplyControlModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.discussion = this.attrs.discussion;

    this.users = Stream([]);
    this.loading = false;

    this.mode = Stream(this.discussion?.replyMode() || 0); // 0 = allow all except, 1 = block all except
    this.newMode = Stream(this.discussion?.replyMode() || 0);

    this.newUser = Stream('');
    this.searching = false;

    this.page = Stream(1);
    this.perPage = 10;
    this.hasMore = Stream(true);

    this.loadingInitial = true;
    this.loadingMore = false;
    this.savingMode = false;
    this.updatingUsers = false;

    this.loadInitialUsers();
  }

  loadInitialUsers() {
    this.loadingInitial = true;
    this.page(1);
    this.hasMore(true);
    this.users([]);

    this.loadUsers().finally(() => {
      this.loadingInitial = false;
    });
  }

  loadUsers() {
    if (this.loadingMore || !this.hasMore()) return;

    this.loadingMore = true;

    return app.request({
      method: 'GET',
      url: `/api/discussions/${this.discussion.id()}/reply-control-users`,
      params: {
        page: this.page(),
      },
    }).then(response => {
      const users = response.data.map(userData => {
        const user = new User();
        user.pushData(userData);
        return user;
      }) || [];
      
      if (users.length < this.perPage) {
        this.hasMore(false);
      }

      this.users(this.users().concat(users));
      this.page(this.page() + 1);
    }).catch()
      .finally(() => {
      this.loadingMore = false;
      m.redraw();
    });
  }

  addUser(user) {
    if (!user) return;

    this.updatingUsers = true;

    app.request({
      method: 'POST',
      url: `/api/discussions/${this.discussion.id()}/reply-control-users`,
      body: {
        data : {
          userId: user.id()
        }
      }
    }).then(() => {
      this.newUser('');
      if (!this.users().some(u => u.id() === user.id())) {
        this.users([...this.users(), user]);
      }
    }).catch()
      .finally(() => {
      this.updatingUsers = false;
      m.redraw();
    });
  }

  removeUser(user) {
    if (!user) return;

    this.updatingUsers = true;

    app.request({
      method: 'DELETE',
      url: `/api/discussions/${this.discussion.id()}/reply-control-users/${user.id()}`
    }).then(() => {
      this.users(this.users().filter(u => u.id() !== user.id()));
    }).catch()
      .finally(() => {
      this.updatingUsers = false;
      m.redraw();
    });
  }

  saveMode() {
    if (this.newMode() === this.mode() || ![0, 1].includes(this.newMode())) {
      return;
    }

    this.savingMode = true;

    app.request({
      method: 'PATCH',
      url: `/api/discussions/${this.discussion.id()}/reply-control-mode`,
      body: { mode: this.newMode() }
    }).then(response => {
      this.discussion.pushData(response.data);
      this.mode(this.newMode());
    }).catch()
      .finally(() => {
      this.savingMode = false;
      m.redraw();
    });
  }

  checkScroll(container) {
    if (this.loadingMore || !this.hasMore()) return;

    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;

    if (nearBottom) {
      this.loadUsers();
    }
  }

  title() {
    return app.translator.trans('lltq-reply-control.forum.reply_control');
  }

  className() {
    return 'ReplyControlModal Modal--small';
  }

  content() {
    return [
      <div className="Modal-body">
        <div className="Form-group">
          <label for="reply-control-mode">{app.translator.trans('lltq-reply-control.forum.access_mode')}:</label>
          <select
            id="reply-control-mode"
            className="FormControl"
            onchange={(e) => this.newMode(parseInt(e.target.value))}
            value={this.newMode()}
          >
            <option value="0">{app.translator.trans('lltq-reply-control.forum.allow_all_except')}</option>
            <option value="1">{app.translator.trans('lltq-reply-control.forum.block_all_except')}</option>
          </select>
          <Button
            className="Button Button--primary"
            onclick={() => this.saveMode()}
            loading={this.savingMode}
            disabled={this.savingMode || this.newMode() === this.mode()}
            style="margin-top: 10px;"
          >
            {app.translator.trans('lltq-reply-control.forum.save')}
          </Button>
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('lltq-reply-control.forum.add_user')}</label>
          <UserSearch
            placeholder={app.translator.trans('lltq-reply-control.forum.username_placeholder')}
            onchange={user => this.addUser(user)}
          />
        </div>

        <div className="Form-group">
          <strong>{app.translator.trans('lltq-reply-control.forum.current_users')}:</strong>
          {this.loadingInitial && <div>Loading...</div>}
          <div
            className="UserList-scroll"
            style={{ maxHeight: '300px', overflowY: 'auto' }}
            oncreate={vnode => {
              vnode.dom.addEventListener('scroll', () => this.checkScroll(vnode.dom));
            }}
          >
            <ul className="ReplyControlModal-user-list">
              {this.users().map(user => (
                <li className="ReplyControlModal-user" key={user.id()}>
                  {user.avatarUrl() ? (
                    <img src={user.avatarUrl()} alt={user.username()} className="Avatar" />
                    ) : (
                      <span className="Avatar" title={user.username()} style={{ background: user.color?.() || '#ccc' }}>
                        {user.username().charAt(0).toUpperCase()}
                      </span>
                    )
                  }
                  <span className="ReplyControlModal-user-info">
                    <strong>{user.displayName?.() || user.username()}</strong>
                    <span className="username">@{user.username()}</span>
                  </span>
                  <Button
                    className="Button Button--icon Button--danger"
                    icon="fas fa-times"
                    onclick={() => this.removeUser(user)}
                    disabled={this.updatingUsers}
                    aria-label="Remove"
                  />
                </li>
              ))}
            </ul>
            {this.loadingMore && <div>Loading more...</div>}
          </div>
        </div>
      </div>
    ];
  }
}
